import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { acquireBrowserProof, type BrowserProofResult } from '@neumind/shared/react';
import { Section } from '../components/ui/Section';
import { SectionHeader } from '../components/ui/SectionHeader';
import { Hero } from '../components/ui/Hero';
import { CalendlyWidget } from '../components/widgets/CalendlyWidget';
import { usePostHog } from '../components/widgets/PostHogProvider';
import { ExternalUrls, ContactInfo } from '../lib/constants';
import { useSEO, useBreadcrumbSchema, PageSEO } from '../lib/seo';

const CONTACT_FORM_API_URL = import.meta.env.VITE_CONTACT_FORM_API_URL;
// Public Turnstile sitekey (ISS-260819-epq3 Phase 3, spec §5/§6). Safe in a
// client bundle — the paired secret key lives only in the contact Lambda's
// environment, never here. A production deploy without this set fails
// before build (.github/actions/deploy-static-sites/action.yml guard);
// acquireBrowserProof() itself resolves `no_site_key` immediately if it's
// ever empty, so an unconfigured non-production build degrades safely.
const TURNSTILE_CONTACT_SITE_KEY = import.meta.env.VITE_TURNSTILE_CONTACT_SITE_KEY;

export const Route = createFileRoute('/contact-us')({
  component: ContactPage,
});

interface ContactForm {
  name: string;
  email: string;
  message: string;
  acceptTerms: boolean;
  // Honeypot (spec D3) — a genuine visitor never sees or fills this field
  // (see the hidden wrapper around its <input> below). Always sent, even
  // empty, so the server's isHoneypotFilled() has a value to check.
  companyWebsite: string;
}

// Material correction 3 (spec §3 item 3): production must never pretend a
// submission succeeded when CONTACT_FORM_API_URL is unset — the previous
// mailto: fallback did exactly that (window.location.href + success:true
// with no delivery guarantee at all). This function is only ever called
// once the caller has confirmed CONTACT_FORM_API_URL is present; the
// unconfigured case renders an honest link instead of calling this at all
// (see ContactPage below).
async function submitContactForm(
  data: ContactForm,
  proofResult: BrowserProofResult,
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(CONTACT_FORM_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: data.name,
      email: data.email,
      message: data.message,
      companyWebsite: data.companyWebsite,
      // Omit the key entirely on acquisition failure — the server's
      // extractBrowserProofToken() only recognises a non-empty string, and
      // an omitted/undefined field reads the same as "no proof" to it
      // (spec §5 "Contact client/server": acquisition failure still
      // submits; the server's CONTACT_PROOF_MODE decides disposition).
      ...(proofResult.ok ? { browserProof: proofResult.token } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    return { success: false, error: errorData.error || 'Failed to send message' };
  }

  return { success: true };
}

export function ContactPage() {
  useSEO(PageSEO.contact);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Contact Us', url: '/contact-us' }
  ]);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { capture } = usePostHog();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactForm>();

  // Hide Chatwoot bubble on contact-us page
  useEffect(() => {
    if (window.$chatwoot) {
      window.$chatwoot.toggleBubbleVisibility('hide');
    }
    // Also try on chatwoot:ready event in case it loads after mount
    const handleReady = () => {
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility('hide');
      }
    };
    window.addEventListener('chatwoot:ready', handleReady);

    return () => {
      window.removeEventListener('chatwoot:ready', handleReady);
      // Show bubble again when leaving the page
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility('show');
      }
    };
  }, []);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    setSubmitError(null);

    // Acquire a fresh proof immediately before the side-effecting request
    // (spec §5 "Contact client/server"). Never blocks submission on
    // failure — the server's CONTACT_PROOF_MODE is the only place that
    // decides what an invalid/missing proof means (spec D3).
    const proofResult = await acquireBrowserProof({
      siteKey: TURNSTILE_CONTACT_SITE_KEY,
      action: 'contact_submit',
    });

    capture('contact_form_submitted', {
      has_message: data.message.length > 0,
      message_length: data.message.length,
      // No PII — either the fixed literal 'ok' or one of
      // BrowserProofFailure['reason']'s closed enum values.
      browser_proof: proofResult.ok ? 'ok' : proofResult.reason,
    });

    const result = await submitContactForm(data, proofResult);

    setIsSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      setSubmitError(result.error || 'Failed to send message. Please try again.');
    }
  };

  const handleExternalLinkClick = (linkType: 'calendly' | 'support_hub' | 'email', href: string) => {
    capture('contact_link_clicked', {
      link_type: linkType,
      href,
    });
  };

  return (
    <>
      <Hero
        title="Get in Touch"
        subtitle="Have questions? Want a demo? We'd love to hear from you."
        variant="gradient"
        showScrollIndicator
      />
      <Section>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <SectionHeader
              title="Send us a message"
              titleVariant="h3"
              sx={{ mb: 3 }}
            />

            {!CONTACT_FORM_API_URL ? (
              // Material correction 3 (spec §3 item 3) — no more mailto:
              // fallback pretending a submission succeeded. A production
              // deploy is guarded against ever reaching this branch
              // (.github/actions/deploy-static-sites/action.yml fails the
              // deploy first), so seeing it live would mean that guard was
              // bypassed; either way, this is the honest state either
              // build can show: a direct, visible way to reach us instead
              // of a form that cannot actually deliver.
              <Alert severity="info" sx={{ mb: 3 }}>
                {import.meta.env.PROD ? (
                  <>
                    We're unable to load the contact form right now. Email us
                    directly at{' '}
                    <MuiLink href={ContactInfo.emailLink}>{ContactInfo.email}</MuiLink>.
                  </>
                ) : (
                  <>
                    The contact form API isn't configured for this
                    environment. Email us directly at{' '}
                    <MuiLink href={ContactInfo.emailLink}>{ContactInfo.email}</MuiLink>.
                  </>
                )}
              </Alert>
            ) : submitted ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thank you for your message! We'll get back to you soon.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                {/* Honeypot (spec D3) — a genuine visitor never sees, tabs
                    to, or fills this field; any non-empty value quarantines
                    the submission server-side (isHoneypotFilled in the
                    contact Lambda requires the field to be present in the
                    payload AND non-empty — this component always sends it,
                    even empty, so that precondition holds). Off-screen
                    positioning, not display:none, is deliberate: some
                    unsophisticated bots check getComputedStyle().display
                    before filling a field specifically to dodge
                    display:none honeypots, so an absolute off-screen
                    position still reads as "fillable" to that class of bot
                    while never being visible or reachable by a real person
                    — aria-hidden + tabIndex={-1} handle keyboard/AT users.
                    autoComplete="off" stays as basic hygiene, but it is NOT
                    a real defence (codex review P2, 2026-08-25 — dropped a
                    prior claim that it stops password-manager fill): major
                    browsers and password managers routinely ignore
                    autocomplete="off" for generic autofill heuristics, so
                    an aggressive autofill CAN still populate this field on a
                    genuine visitor and quarantine their real enquiry. That
                    residual risk is accepted, not eliminated — recoverable
                    via the Gate 5 daily quarantine-mailbox review (spec §8
                    Gate 5, first week), not a client-side fix. */}
                <Box
                  aria-hidden="true"
                  sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    data-testid="contact-honeypot-company-website"
                    {...register('companyWebsite')}
                  />
                </Box>
                {submitError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {submitError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="Name"
                  {...register('name', { required: 'Name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isSubmitting}
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                  sx={{ mb: 3 }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  {...register('message', { required: 'Message is required' })}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                  disabled={isSubmitting}
                  sx={{ mb: 3 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('acceptTerms', {
                        required: 'You must accept the terms',
                      })}
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I accept the{' '}
                      <MuiLink href="/terms" target="_blank">
                        Terms of Service
                      </MuiLink>{' '}
                      and{' '}
                      <MuiLink href="/privacy" target="_blank">
                        Privacy Policy
                      </MuiLink>
                    </Typography>
                  }
                  sx={{ mb: 3 }}
                />
                {errors.acceptTerms && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
                    {errors.acceptTerms.message}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionHeader
              title="Other ways to connect"
              titleVariant="h3"
              sx={{ mb: 3 }}
            />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h4">Book a Demo</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schedule a personalised walkthrough of neumind.
                  </Typography>
                  <MuiLink
                    href={ExternalUrls.calendly}
                    target="_blank"
                    rel="noopener"
                    sx={{ fontSize: '0.875rem' }}
                    onClick={() => handleExternalLinkClick('calendly', ExternalUrls.calendly)}
                  >
                    Book via Calendly →
                  </MuiLink>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HelpCenterIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h4">Support Hub</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse FAQs, guides, and documentation.
                  </Typography>
                  <MuiLink
                    href={ExternalUrls.support}
                    target="_blank"
                    rel="noopener"
                    sx={{ fontSize: '0.875rem' }}
                    onClick={() => handleExternalLinkClick('support_hub', ExternalUrls.support)}
                  >
                    Visit Support Hub →
                  </MuiLink>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h4">Email Us</Typography>
                  <Typography variant="body2" color="text.secondary">
                    For general enquiries and support.
                  </Typography>
                  <MuiLink
                    href={ContactInfo.emailLink}
                    sx={{ fontSize: '0.875rem' }}
                    onClick={() => handleExternalLinkClick('email', ContactInfo.emailLink)}
                  >
                    {ContactInfo.email}
                  </MuiLink>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Section>
      {/* Calendly Section */}
      <Section title="Schedule a Demo" subtitle="Pick a time that works for you." centered variant="gradient">
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <CalendlyWidget
            url={ExternalUrls.calendly}
            styles={{ height: '700px', minWidth: '320px' }}
          />
        </Box>
      </Section>
    </>
  )
}
